import React, {useEffect, useRef, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {
    Alert, Box, Button, Chip, CircularProgress, DialogContent, DialogTitle, IconButton,
    Tooltip, Typography, useMediaQuery, useTheme
} from '@mui/material';
import DataGrid from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import {ArrowBackRounded, ChevronRightRounded, CloseRounded, HistoryOutlined, RefreshRounded} from '@mui/icons-material';
import moment from 'moment';
import {automationRulesServices} from '../../service/automationRulesService';
import {AutomationDialog} from './AutomationDesign';
import {getSettingsColors} from '../settings/SettingsDesign';
import {CustomDataGridBox} from '../dashboard/Dashboard';
import {reportingGridStyles} from '../utils/ReportingToolbarDesign';
import useCustomStyles from '../utils/UseCustomStyles';
import {IsAtBottom} from '../reporting-table/ReactDataGridUtils';
import Automation from './Automation';

export function AutomationLogsPage() {
    return <Automation historyOnly/>;
}

export default function AutomationLogs() {
    const {ruleId} = useParams();
    const history = useHistory();
    const location = useLocation();
    const role = useSelector(state => state.auth?.userSettings?.role);
    const executionId = new URLSearchParams(location.search).get('executionId') || '';
    const close = () => {
        if (location.state?.background) history.goBack();
        else history.replace(role === 'user' ? '/dashboard' : '/automation');
    };
    return <AutomationLogsDialog key={`${ruleId}:${executionId}`} ruleId={ruleId} executionId={executionId}
        ruleName={location.state?.ruleName} notification={executionId ? location.state?.notification : undefined}
        onClose={close} onShowAllRuns={() => history.replace({...location, search: '', state: {...location.state, notification: undefined}})}/>;
}

const actionLabels = {
    pause: 'Pause', enable: 'Enable', increase_daily_budget: 'Increase daily budget', decrease_daily_budget: 'Decrease daily budget', none: 'Notification only'
};
const actionLabel = action => actionLabels[action] || action || '—';
const timestamp = value => value && moment.utc(value).isValid() ? moment.utc(value).format('DD MMM YYYY, HH:mm:ss [UTC]') : '—';
const executionDate = value => /^[1-9]\d{6,18}$/.test(value) ? timestamp(Number(value.slice(0, -6))) : '';
const customStyles = theme => ({dataGrid: {
    ...theme.clicksReportDataGrid,
    '& .automation-log-selected .rdg-cell': {backgroundColor: theme.palette.background.tableSelectedRow}
}});

function createLogColumns(openDetails) {
    return [
        {key: 'eventTime', name: 'Time (UTC)', width: 220, renderCell: ({row}) => moment.utc(row.eventTime).format('DD MMM YYYY, HH:mm:ss')},
        {key: 'campaignId', name: 'Campaign', width: 100, renderCell: ({row}) => `#${row.campaignId}`},
        {key: 'action', name: 'Action', width: 200, renderCell: ({row}) => actionLabel(row.action)},
        {key: 'status', name: 'Status', width: 110, renderCell: ({row}) => <LogStatus status={row.status}/>},
        {key: 'durationMs', name: 'Duration', width: 110, renderCell: ({row}) => `${row.durationMs.toLocaleString()} ms`},
        {key: 'details', name: 'Details', width: 85, renderCell: ({row}) => <IconButton size="small"
            aria-label={`View ${actionLabel(row.action)} details for campaign ${row.campaignId}`}
            onClick={event => {event.stopPropagation(); openDetails(row);}}><ChevronRightRounded fontSize="small"/></IconButton>}
    ];
}

function LogStatus({status}) {
    const color = {success: 'primary', failed: 'error', skipped: 'default'}[status] || 'default';
    return <Chip size="small" color={color} variant="outlined" label={status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
        sx={{height: 24, fontSize: 12}}/>;
}

function formatBody(value) {
    if (!value) return 'No body recorded.';
    try {return JSON.stringify(JSON.parse(value), null, 2);} catch {return value;}
}

export function AutomationLogsDialog({ruleId, executionId = '', ruleName, notification, onClose, onShowAllRuns}) {
    const theme = useTheme();
    const classes = useCustomStyles(customStyles, theme);
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const compact = useMediaQuery(theme.breakpoints.down('md'));
    const [rule, setRule] = useState(null);
    const [pagination, setPagination] = useState({page: 1, limit: 25, append: false});
    const [revision, setRevision] = useState(0);
    const [result, setResult] = useState({items: [], page: 0, totalRecords: 0, recordsPerPage: 25, recordsReturned: 0});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null);
    const [columns, setColumns] = useState(() => createLogColumns(setSelected));
    const gridRef = useRef(null);
    const lastScrollTop = useRef(0);
    const validRule = /^[1-9]\d{0,19}$/.test(String(ruleId));
    const validExecution = !executionId || /^[1-9]\d{0,18}$/.test(executionId);

    useEffect(() => {
        let active = true;
        if (validRule) automationRulesServices.find(ruleId).then(value => {
            if (active) setRule(value);
        }).catch(() => {}); // The logs request reports authorization and storage errors.
        return () => {active = false;};
    }, [ruleId, validRule]);

    useEffect(() => {
        let active = true;
        setError('');
        if (!pagination.append) {
            setSelected(null);
            lastScrollTop.current = 0;
            gridRef.current?.element?.scrollTo?.(0, 0);
        }
        if (!validRule || !validExecution) {
            setError('This action-history link is invalid.');
            setLoading(false);
            return () => {active = false;};
        }
        setLoading(true);
        automationRulesServices.readActionLogs({ruleId, ...(executionId ? {executionId} : {}), page: pagination.page, limit: pagination.limit}).then(value => {
            if (!active) return;
            const lastPage = Math.max(1, Math.ceil(value.totalRecords / value.recordsPerPage));
            if (pagination.page > lastPage) {
                setPagination(previous => ({...previous, page: lastPage, append: false}));
                return;
            }
            // Retries can share an idempotency key and timestamps, so key each recorded row separately.
            const items = value.items.map((item, index) => ({...item, _logRowId: `${value.page}:${index}`}));
            setResult(previous => ({...value, items: pagination.append ? [...previous.items, ...items] : items}));
        }).catch(cause => {
            if (active) setError(cause?.response?.data?.error || 'Unable to load action logs. Please try again.');
        }).finally(() => {if (active) setLoading(false);});
        return () => {active = false;};
    }, [ruleId, executionId, pagination, revision, validRule, validExecution]);

    const name = rule?.name || ruleName || `Rule #${ruleId}`;
    const showDetails = Boolean(selected);
    const summary = notification?.data;
    const summaryMatches = String(summary?.ruleId) === String(ruleId) && String(summary?.executionId) === executionId;
    const totalPages = Math.max(1, Math.ceil(result.totalRecords / (result.recordsPerPage || pagination.limit)));
    const hasMore = result.page > 0 && result.recordsReturned > 0 && result.page < totalPages;
    const loadingMore = loading && pagination.append;
    const refresh = () => {setPagination(previous => ({...previous, page: 1, append: false})); setRevision(value => value + 1);};
    const handleScroll = event => {
        const {scrollTop} = event.currentTarget;
        const scrollingDown = scrollTop > lastScrollTop.current;
        lastScrollTop.current = scrollTop;
        if (scrollingDown && IsAtBottom(event) && !loading && !error && hasMore) {
            setPagination(previous => ({...previous, page: result.page + 1, append: true}));
        }
    };
    const handleColumnsReorder = (sourceKey, targetKey) => setColumns(previous => {
        const reordered = [...previous];
        const sourceIndex = reordered.findIndex(column => column.key === sourceKey);
        const targetIndex = reordered.findIndex(column => column.key === targetKey);
        if (sourceIndex < 0 || targetIndex < 0) return previous;
        const [source] = reordered.splice(sourceIndex, 1);
        reordered.splice(targetIndex, 0, source);
        return reordered;
    });

    return <AutomationDialog open fullWidth fullScreen={fullScreen} maxWidth="xl" onClose={onClose}
        aria-labelledby="automation-logs-title" sx={{
            '& .MuiDialog-paper': {height: fullScreen ? '100%' : 'min(820px, calc(100% - 48px))', maxWidth: 1440},
            '& .MuiDialogContent-root': {p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}
        }}>
        <DialogTitle id="automation-logs-heading">
            <Box component="span" sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                <Box component="span" id="automation-logs-title" sx={{display: 'flex', alignItems: 'center', gap: 1}}><HistoryOutlined color="primary"/>Action logs</Box>
                <IconButton aria-label="Close action logs" onClick={onClose} size="small"><CloseRounded/></IconButton>
            </Box>
        </DialogTitle>
        <Box sx={{px: {xs: 2, sm: 3}, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
            <Box sx={{flex: 1, minWidth: 0}}>
                <Typography sx={{fontWeight: 600, fontSize: 15, overflowWrap: 'anywhere'}}>{name}</Typography>
                <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.5, overflowWrap: 'anywhere'}}>
                    {executionId ? `Execution · ${executionDate(executionId) || executionId}` : 'All executions · Newest first'}
                </Typography>
            </Box>
            {executionId && <Button size="small" onClick={onShowAllRuns}>Show all executions</Button>}
            <Tooltip title="Refresh action logs"><span><IconButton aria-label="Refresh action logs" disabled={loading}
                onClick={refresh}><RefreshRounded/></IconButton></span></Tooltip>
        </Box>
        <DialogContent>
            {summaryMatches && <Box sx={{px: {xs: 2, sm: 3}, pb: 2}}>
                <Alert severity={['success', 'warning', 'error', 'info'].includes(notification.severity) ? notification.severity : 'info'}>
                    <Typography sx={{fontSize: 13}}>{notification.message}</Typography>
                </Alert>
            </Box>}
            <Box sx={{display: 'grid', gridTemplateColumns: showDetails && !compact ? 'minmax(0, 1fr) 380px' : 'minmax(0, 1fr)',
                flex: 1, minHeight: 0, borderTop: 1, borderColor: 'divider'}}>
                {(!showDetails || !compact) && <Box sx={{minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative'}}>
                    {error && <Box sx={{p: 3}}><Alert severity="error" action={validRule && validExecution ?
                        <Button color="inherit" onClick={() => setRevision(value => value + 1)}>Retry</Button> : undefined}>{error}</Alert></Box>}
                    {loading && !pagination.append ? <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 6}}>
                            <CircularProgress size={28} aria-label="Loading action logs"/>
                        </Box> : !error && result.items.length === 0 ? <Box role="status" sx={{p: 4, textAlign: 'center', flex: 1}}>
                            <HistoryOutlined sx={{fontSize: 36, color: 'text.secondary', mb: 1}}/>
                            <Typography sx={{fontWeight: 600}}>No action logs recorded</Typography>
                            <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 1, lineHeight: 1.7}}>
                                {executionId ? 'This execution has no recorded external actions.' : 'External action results will appear here after this rule runs.'}
                                {' '}Notification-only rules and skipped actions do not create action logs.
                            </Typography>
                        </Box> : result.items.length > 0 ? <CustomDataGridBox sx={reportingGridStyles}>
                            <DataGrid ref={gridRef} aria-label="Automation action logs" columns={columns} rows={result.items}
                                rowKeyGetter={row => row._logRowId}
                                defaultColumnOptions={{sortable: false, resizable: true, draggable: true}}
                                onColumnsReorder={handleColumnsReorder}
                                onColumnResize={(index, width) => setColumns(previous => previous.map((column, columnIndex) => columnIndex === index ? {...column, width} : column))}
                                onCellClick={({row}) => setSelected(row)}
                                onSelectedCellChange={({rowIdx, row}) => {if (rowIdx >= 0) setSelected(row);}}
                                rowClass={row => selected?._logRowId === row._logRowId ? 'automation-log-selected' : undefined}
                                onScroll={handleScroll} enableVirtualization={false} className={classes.dataGrid}/>
                        </CustomDataGridBox> : null}
                    {loadingMore && <Box sx={{position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none'}}>
                        <CircularProgress size={20} aria-label="Loading more action logs"/>
                    </Box>}
                </Box>}
                {showDetails && <ActionDetails log={selected} ruleName={name} compact={compact} onClose={() => setSelected(null)}/>}
            </Box>
        </DialogContent>
    </AutomationDialog>;
}

function ActionDetails({log, ruleName, compact, onClose}) {
    const fields = [
        ['Rule', ruleName], ['Campaign', `#${log.campaignId}`], ['Entity', {campaign: 'Campaign', adset: 'Ad set/group', ad: 'Ad'}[log.entityType] || log.entityType],
        ['External ID', log.externalEntityId], ['Action', actionLabel(log.action)], ['HTTP status', log.httpStatus || 'No response'],
        ['Duration', `${log.durationMs.toLocaleString()} ms`], ['Started', timestamp(log.eventTime)], ['Finished', timestamp(log.finishedAt)]
    ];
    return <Box component="aside" aria-label="Action details" sx={{p: 2.5, overflow: 'auto', minHeight: 0,
        borderLeft: compact ? 0 : 1, borderColor: 'divider', bgcolor: theme => getSettingsColors(theme).inset}}>
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 2}}>
            {compact && <IconButton autoFocus aria-label="Back to action logs" onClick={onClose} size="small"><ArrowBackRounded/></IconButton>}
            <Typography component="h3" sx={{fontSize: 17, fontWeight: 600, flex: 1}}>Action details</Typography>
            {!compact && <IconButton aria-label="Close action details" onClick={onClose} size="small"><CloseRounded/></IconButton>}
        </Box>
        <LogStatus status={log.status}/>
        <Box component="dl" sx={{display: 'grid', gridTemplateColumns: '110px minmax(0, 1fr)', columnGap: 2, rowGap: 1.5, my: 2.5,
            '& dt': {fontSize: 12, color: 'text.secondary'}, '& dd': {m: 0, fontSize: 12, overflowWrap: 'anywhere'}}}>
            {fields.map(([label, value]) => <React.Fragment key={label}><Box component="dt">{label}</Box><Box component="dd">{value ?? '—'}</Box></React.Fragment>)}
        </Box>
        {log.errorMessage && <Alert severity="error" sx={{mb: 2, overflowWrap: 'anywhere'}}>{log.errorMessage}</Alert>}
        <LogBody label="Request" value={log.requestJson}/>
        <LogBody key={log.idempotencyKey + log.eventTime} label="Response" value={log.responseBody} expanded={log.status === 'failed'}/>
        <Box component="details" sx={{fontSize: 12, mt: 2, '& summary': {cursor: 'pointer', fontWeight: 600}, '& p': {fontSize: 12, mt: 1}}}>
            <summary>Context</summary>
            <Typography>Workspace ID: {log.workspaceId}</Typography>
            <Typography>Traffic source ID: {log.trafficSourceId}</Typography>
            <Typography>Integration ID: {log.integrationId}</Typography>
        </Box>
        <Box sx={{mt: 2}}><Typography sx={{fontSize: 12, color: 'text.secondary'}}>Request key</Typography>
            <Typography sx={{fontSize: 11, mt: 0.5, overflowWrap: 'anywhere', fontFamily: 'monospace'}}>{log.idempotencyKey}</Typography>
        </Box>
    </Box>;
}

function LogBody({label, value, expanded = false}) {
    return <Box component="details" open={expanded || undefined} sx={{border: 1, borderColor: 'divider', borderRadius: '8px', mb: 1.5, overflow: 'hidden'}}>
        <Box component="summary" sx={{p: 1.5, fontSize: 13, fontWeight: 600, cursor: 'pointer'}}>{label}</Box>
        <Box component="pre" sx={{m: 0, p: 1.5, fontSize: 11, lineHeight: 1.7, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
            bgcolor: theme => getSettingsColors(theme).card}}>{formatBody(value)}</Box>
    </Box>;
}
